import { useRef } from "react";

import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import ChatInputFiles from "@/components/chat/chat-input/ChatInputFiles";
import ChatInputSettings from "@/components/chat/chat-input/ChatInputSettings";
import { FormButton } from "@/components/form/FormButton";
import { Icon } from "@/components/icons/Icon";
import { ActiveTooltip, TooltipShortcut } from "@/components/tooltip/Tooltip";
import { displayHotkey, HOTKEY_MAP } from "@/utils/hotkeys";
import { twMerge } from "@/utils/twMerge";
import { useRovingNav } from "@/utils/useRovingNav";

import { ChatModelSelector } from "./ChatModelSelector";
import { ChatUploadButton } from "./ChatUploadButton";

import type { ChatModel } from "@/models/chat/ChatModel";

export const ChatInputButtons = observer(function ChatInputButtons({
  chat,
  isCollapsed,
  isComposerEmpty,
  isSendDisabled,
  isFloatingInput,
}: {
  chat: ChatModel;
  isCollapsed: boolean;
  isComposerEmpty: boolean;
  isSendDisabled: boolean;
  isFloatingInput: boolean;
}) {
  const t = useTranslations();
  const toolbarRef = useRef<HTMLDivElement>(null);
  useRovingNav(toolbarRef, "horizontal");

  let syncErrorMessage: string | undefined;
  if (chat.allowBotWriteToDocumentSyncError) {
    syncErrorMessage = t("chat.allowWriteToDocumentSyncError");
  } else if (chat.selectedBotModelSyncError) {
    syncErrorMessage = t("chat.selectedModelSyncError");
  }

  const showStopButton = chat.withBot && chat.isBotResponding && isComposerEmpty;

  const handleSubmit = () => {
    if (showStopButton) {
      chat.requestBotStop().catch((err) => {
        console.error("Failed to stop chat bot response:", err);
      });
      return;
    }

    chat
      .sendMessage(isFloatingInput ? "document_floating_input" : "chat_footer_button")
      .catch((err) => {
        console.error("Failed to send chat message:", err);
      });
  };

  return (
    <div
      ref={toolbarRef}
      role="toolbar"
      aria-orientation="horizontal"
      aria-label={t("chat.inputActions")}
      className="relative flex justify-end gap-1.5 w-full p-2.5 pt-0 -mt-1.5 pointer-events-none"
    >
      {syncErrorMessage && (
        <p
          role="alert"
          className="absolute -top-5 left-2.5 right-2.5 text-size-12 text-error text-center"
        >
          {syncErrorMessage}
        </p>
      )}
      <div
        className={twMerge(
          "flex gap-1 items-center min-w-0 transition-[flex-grow] duration-200 *:pointer-events-auto",
          isCollapsed ? "grow-0" : "grow",
        )}
      >
        <ChatUploadButton chat={chat} />
        {!isCollapsed && (
          <>
            {chat.withBot && <ChatInputSettings chat={chat} />}
            <ChatInputFiles chat={chat} />
          </>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0 *:pointer-events-auto">
        {!isCollapsed && chat.withBot && <ChatModelSelector chat={chat} />}
        <ActiveTooltip
          focusDisabled
          disabled={!showStopButton && isSendDisabled}
          title={
            showStopButton ? (
              t("chat.stop")
            ) : (
              <>
                {t("chat.sendMessage")}
                <TooltipShortcut>{displayHotkey("send-message")}</TooltipShortcut>
              </>
            )
          }
        >
          <FormButton
            onClick={handleSubmit}
            aria-label={showStopButton ? t("chat.stop") : t("chat.send")}
            aria-keyshortcuts={showStopButton ? undefined : HOTKEY_MAP["send-message"]}
            variant="accent"
            className={twMerge(
              "rounded-8 w-8 h-8 p-0",
              !showStopButton && isSendDisabled && "pointer-events-none!",
            )}
            disabled={!showStopButton && isSendDisabled}
          >
            <Icon
              name={showStopButton ? "Stop" : "ArrowUp"}
              className="m-auto [--color-icon-main:var(--color-icon-active)]"
            />
          </FormButton>
        </ActiveTooltip>
      </div>
    </div>
  );
});
